import React from 'react';
import { set, get } from 'lodash-es';
import { useIntl } from 'react-intl';
import { useStaticQuery, graphql } from 'gatsby';

import { pathUnslash, parsePathname } from 'lib/url';
import { useLang } from 'lib/langContext';

import type { Target, Tab } from './types';
import NextPrevious from './NextPreviousStyles';

type PageData = Pick<Queries.MdxFrontmatter, 'title' | 'menuLabel' | 'tabs'>;
type PageLang = string; // e.g. en
type PageSlug = string; // e.g. /docs/overview
type PageSet = Record<PageLang, PageData>;
type AllPagesData = Record<PageSlug, PageSet>;

type NavigationItem = {
    category: string;
    title: null | string;
    path: string;
};
type UseNavigationData = {
    pathPrefix: string;
    navigationList: NavigationItem[];
    allPagesData: AllPagesData;
};
function useNavigationData(): UseNavigationData {
    const data = useStaticQuery<Queries.UseNavigationDataHookQuery>(graphql`
        query UseNavigationDataHook {
            allMdx {
                edges {
                    node {
                        fields {
                            slug
                            lang
                            path
                        }
                        frontmatter {
                            tabs {
                                slug
                                title
                            }
                            title
                            menuLabel
                        }
                    }
                }
            }
            allNavItemsYaml {
                edges {
                    node {
                        title
                        pages {
                            path
                        }
                    }
                }
            }
            site {
                pathPrefix
            }
        }
    `);
    const lang = useLang();

    // Collect page data from all MDX nodes
    const allPagesData: AllPagesData = {};
    data.allMdx.edges.forEach(({ node }) => {
        const { fields, frontmatter } = node;
        if (!fields?.slug || !fields.lang) return;
        set(allPagesData, [fields.slug, fields.lang], frontmatter as PageData);
    });

    // Collect navigation items from all NavItemsYaml nodes
    // and fill-in language-dependent data from the MDX nodes
    const navigationList: NavigationItem[] = data.allNavItemsYaml.edges.flatMap(({ node }) => {
        const res: NavigationItem[] = [];

        node.pages?.forEach(page => {
            if (!page?.path) return;

            const slug = pathUnslash(page.path);
            const langKey = lang.current.key;
            const pageData = get(allPagesData, [slug, langKey]) as Maybe<PageData>;

            res.push({
                path: page.path,
                title: pageData?.title ?? node.title,
                category: node.title as string,
            });
        });

        return res;
    });

    return {
        navigationList,
        allPagesData,
        pathPrefix: data.site?.pathPrefix ?? '',
    };
}

type NavigationItems = {
    prevCategory: null | NavigationItem;
    nextCategory: null | NavigationItem;
    navIndex: number;
};
function useNavigationItems(tabs: Maybe<Tab[]>, pathname: string): NavigationItems {
    const { navigationList, pathPrefix } = useNavigationData();
    const unPrefixedPathname = pathname.replace(pathPrefix, '');

    const currentNavigationItem = tabs
        ? unPrefixedPathname.replace(/\/[^/]*\/?$/, '') // removes the last url segment
        : unPrefixedPathname.replace(/\/$/, ''); // removes the last slash

    const navIndex = navigationList.findIndex(item => item.path.includes(currentNavigationItem));

    return {
        prevCategory: navigationList[navIndex - 1],
        nextCategory: navigationList[navIndex + 1],
        navIndex,
    };
}

type TabItem = { title: string; slug: string; isCurrentTab: boolean };
type TabItems = {
    prevTabItem: null | TabItem;
    nextTabItem: null | TabItem;
};
function getTabItems(tabs: Maybe<Tab[]>, hrefSegments: string[]): TabItems {
    if (!tabs) return { prevTabItem: null, nextTabItem: null };

    const tabItems: TabItem[] = tabs.map(({ slug, title }) => {
        const isCurrentTab = hrefSegments.at(-1) === slug;
        return { title, slug, isCurrentTab };
    });
    const currentTabIndex = tabItems.findIndex(tab => tab.isCurrentTab);

    return {
        prevTabItem: tabItems[currentTabIndex - 1],
        nextTabItem: tabItems[currentTabIndex + 1],
    };
}
function getEntry(navItem: NavigationItem, lang: string): { to: string; name: string } {
    return {
        to: [lang, navItem.path].join('/').replace(/\/+/g, '/'),
        name: `${navItem.category}${navItem.title ? `: ${navItem.title}` : ''}`,
    };
}

export interface NextPreviousContainerProps {
    tabs: Maybe<Tab[]>;
    location: Location;
    isHomepage?: boolean;
    pageContext: {
        frontmatter?: Queries.MdxFrontmatter;
    };
}
export default function NextPreviousContainer(props: NextPreviousContainerProps) {
    const { tabs, location, isHomepage, pageContext } = props;

    const { formatMessage } = useIntl();
    const lang = useLang();

    const { navigationList, pathPrefix } = useNavigationData();

    // - Language prefix has to be removed from the pathname
    //   used for matching as it doesn't exist in the lookup set
    const parsedPathname = parsePathname(location.pathname, { pathPrefix, langKey: lang.current.key });
    const hrefSegments = parsedPathname.slug.split('/');
    const filteredPathname = hrefSegments.join('/');

    const homeString = formatMessage({ defaultMessage: 'Home' });
    const currentTitle: string = pageContext.frontmatter?.menuLabel ?? pageContext.frontmatter?.title ?? homeString;

    const { prevTabItem, nextTabItem } = getTabItems(tabs, hrefSegments);
    const { prevCategory, nextCategory, navIndex } = useNavigationItems(tabs, filteredPathname);

    if (currentTitle !== 'Home' && !navigationList[navIndex]) return null;

    const getPreviousItem = (): null | Target => {
        if (prevTabItem) {
            const previousSegments = [...hrefSegments.slice(0, -1), prevTabItem.slug];
            const navItem: NavigationItem = { ...navigationList[navIndex], path: `/${previousSegments.join('/')}` };

            return getEntry(navItem, lang.current.key);
        }
        if (prevCategory) return getEntry(prevCategory, lang.current.key);

        return getEntry({ path: '/', title: homeString, category: '' }, lang.current.key);
    };
    const getNextItem = (): null | Target => {
        if (currentTitle === homeString) return getEntry(navigationList[navIndex], lang.current.key);
        if (nextTabItem && nextTabItem.slug) {
            const nextSegments = [...hrefSegments.slice(0, -1), nextTabItem.slug];
            const navItem: NavigationItem = { ...navigationList[navIndex], path: `/${nextSegments.join('/')}` };
            return getEntry(navItem, lang.current.key);
        }

        if (nextCategory) return getEntry(nextCategory, lang.current.key);

        return null; // final page
    };

    return <NextPrevious isHomepage={isHomepage} previousItem={getPreviousItem()} nextItem={getNextItem()} />;
}
